import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, LargeTopStepper, Container, Typography} from 'views/components';
import {Translate, translate, useNavigate, useParams} from 'views/module';
import {KeyboardArrowLeft, KeyboardArrowRight} from 'views/icons';

import backend from 'utils/backend';

import STEPS from './steps';

import moment from 'moment-timezone';

const EditJob = (props) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [newJob, setNewJob] = React.useState({});
  const [isStepValid, setIsStepValid] = React.useState(false);
  const [selectedCaregivers, setSelectedCaregivers] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  const {statusProgressWrap} = props;
  const navigate = useNavigate();
  const params = useParams();
  const jobRn = params.jobRn;

  // Load existing job data
  React.useEffect(() => {
    if (!jobRn) return;

    statusProgressWrap(async () => {
      const jobData = await backend.career.getJob(jobRn, false, true);
      const job = jobData.job;
      const client = jobData.client;

      let clientObj;
      if (!job.client.clientRn) {
        clientObj = {
          clientRn: 'null',
          clientName: job.client.unregisteredClient,
        };
      } else {
        clientObj = {
          clientRn: job.client.clientRn,
          clientName: client.clientDetail?.clientName || '',
        };
      }
      const transformedJob = {
        client: clientObj,
        description: {
          long: {
            blob: job.description.long.blob,
            editorType: 'MARKDOWN',
          },
          short: job.description.short,
        },
        schedule: {
          type: job.schedule.type,
          schedule: job.schedule.type === 'RECURRENCE_EVENT' ? {
            weeklySlots: (job.schedule?.recurrenceRule?.weeklySlots).map((slot) => ({
              day: slot.day,
              startTime: moment(slot.startTime),
              endTime: moment(slot.endTime),
            })),
            timezone: job.schedule.recurrenceRule.timezone,
          } : undefined,
        },
        price: {
          hourlyRate: parseFloat(job.price.hourlyRate),
        },
        startDate: {
          startTime: job.startDate.startTime,
        },
        location: {
          neighborhood: job.location.neighborhood,
          city: job.location.city,
        },
        carePlan: {
          careNeeds: job.requirements.careNeeds,
        },
        requirements: {
          certifications: job.requirements.certifications,
          atLeastOneCertifications: job.requirements.atLeastOneCertifications,
          niceToHaveCertifications: job.requirements.niceToHaveCertifications,
          languages: job.requirements.languages,
          niceToHaveLanguages: job.requirements.niceToHaveLanguages,
        },
        considerations: {
          options: job.requirements.considerations,
          additionalConsiderations: {
            blob: job.requirements.additionalConsiderations.blob,
            editorType: 'MARKDOWN',
          },
        },
        priorities: job.priorities,
      };
      setNewJob(transformedJob);
      setIsLoading(false);
    });
  }, [jobRn, statusProgressWrap]);

  const updateJobDetails = (func) => {
    setNewJob((oldJob) => {
      const newDetails = func();
      return {
        ...oldJob,
        ...newDetails,
      };
    });
  };

  const updateJobSchedule = (func) => {
    setNewJob((oldJob) => {
      const schedule = oldJob.schedule || {};
      const newSchedule = func(schedule);
      return {
        ...oldJob,
        schedule: newSchedule,
      };
    });
  };

  const updateJobPrice = (func) => {
    setNewJob((oldJob) => {
      const price = oldJob.price || {};
      const newPrice = func(price);
      return {
        ...oldJob,
        price: newPrice,
      };
    });
  };

  const updateJobStartDate = (func) => {
    setNewJob((oldJob) => {
      const startDate = oldJob.startDate || {};
      const newStartDate = func(startDate);
      return {
        ...oldJob,
        startDate: newStartDate,
      };
    });
  };

  const updateJobLocation = (func) => {
    setNewJob((oldJob) => {
      const location = oldJob.location || {};
      const newLocation = func(location);
      return {
        ...oldJob,
        location: newLocation,
      };
    });
  };

  const updateJobCarePlan = (func) => {
    setNewJob((oldJob) => {
      const carePlan = oldJob.carePlan || {};
      const newCarePlan = func(carePlan);
      return {
        ...oldJob,
        carePlan: newCarePlan,
      };
    });
  };

  const updateJobRequirements = (func) => {
    setNewJob((oldJob) => {
      const requirements = oldJob.requirements || {};
      const newRequirements = func(requirements);
      return {
        ...oldJob,
        requirements: newRequirements,
      };
    });
  };

  const updateJobConsiderations = (func) => {
    setNewJob((oldJob) => {
      const considerations = oldJob.considerations || {};
      const newConsiderations = func(considerations);
      return {
        ...oldJob,
        considerations: newConsiderations,
      };
    });
  };

  const updateJobPriorities = (func) => {
    setNewJob((oldJob) => {
      const priorities = oldJob.priorities;
      const newPriorities = func(priorities);
      return {
        ...oldJob,
        priorities: newPriorities,
      };
    });
  };

  // Handle moving to the next step
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle moving to the previous step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle the final submit
  const handleSubmit = async () => {
    const {schedule, type} = newJob.schedule;
    let weeklyHours = 0;
    if (type === 'LIVEIN_EVENT') {
      weeklyHours = 56;
    } else if (schedule && schedule.weeklySlots) {
      weeklyHours = schedule.weeklySlots.reduce((total, slot) => {
        const start = moment(slot.startTime);
        const end = moment(slot.endTime);
        return total + end.diff(start, 'hours', true);
      }, 0);
    }
    const hourlyRate = newJob.price.hourlyRate;
    const weeklyEarnings = Math.round(hourlyRate * weeklyHours);

    await statusProgressWrap(async () => {
      const formattedJob = {
        client: newJob.client?.clientRn === 'null' ? {
          unregisteredClient: newJob.client.clientName,
        } : {
          clientRn: newJob.client.clientRn,
        },
        description: {
          long: {
            blob: newJob.description?.long?.blob || '',
            editorType: 'MARKDOWN',
          },
          short: newJob.description?.short || '',
        },
        schedule: {
          type: newJob.schedule?.type || '',
          ...(newJob.schedule?.type === 'RECURRENCE_EVENT' && {
            recurrenceRule: {
              weeklySlots: newJob.schedule?.schedule?.weeklySlots || [],
              timezone: newJob.schedule?.schedule?.timezone || '',
            },
          }),
        },
        price: {
          hourlyRate: parseFloat(newJob.price.hourlyRate),
        },
        startDate: {
          startTime: newJob.startDate.startTime,
        },
        location: {
          neighborhood: newJob.location.neighborhood,
          city: newJob.location.city,
        },
        requirements: {
          certifications: newJob.requirements.certifications || [],
          atLeastOneCertifications: newJob.requirements.atLeastOneCertifications || [],
          niceToHaveCertifications: newJob.requirements.niceToHaveCertifications || [],
          languages: newJob.requirements.languages || [],
          niceToHaveLanguages: newJob.requirements.niceToHaveLanguages || [],
          careNeeds: newJob.carePlan.careNeeds,
          considerations: newJob?.considerations?.options || [],
          additionalConsiderations: {
            blob: newJob?.considerations?.additionalConsiderations?.blob || '',
            editorType: 'MARKDOWN',
          },
        },
        priorities: newJob.priorities,
      };

      // Update existing job instead of creating new one
      await backend.career.updateJob(jobRn, formattedJob);
      const jobTitle = translate('career.create.Review.LocationDisplay', {
        neighborhood: newJob.location.neighborhood,
        city: newJob.location.city,
      });

      if (selectedCaregivers.length > 0) {
        const batchSize = 5;
        for (let i = 0; i < selectedCaregivers.length; i += batchSize) {
          const batch = selectedCaregivers.slice(i, i + batchSize);
          await Promise.all(
              batch.map(({caregiverRn}) =>
                backend.notification
                    .sendNotification(caregiverRn, jobRn, jobTitle, weeklyEarnings)
                    .catch(() => {}),
              ),
          );
        }
      }

      navigate(`/career/${jobRn}`);
    });
  };

  // Handle back to job detail page
  const handleBackToListing = () => {
    navigate(`/career/${jobRn}`);
  };

  const CurrentStep = STEPS[activeStep].component;
  const isLastStep = activeStep === STEPS.length - 1;

  let stepText = translate('career.create.Actions.Continue');
  if (isLastStep) {
    stepText = translate('career.edit.Actions.UpdateJob', 'Update Job');
  }

  // Show loading state while job data is being fetched
  if (isLoading) {
    return (
      <Box sx={{minHeight: '100vh', py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Typography variant="h6">
          <Translate expression="career.edit.Loading" />
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{minHeight: '100vh', py: 4}}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{mb: 4}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
            <Box>
              <Typography variant="h4" component="h1" sx={{mb: 1, fontWeight: 'bold'}}>
                <Translate expression="career.edit.Title" defaultValue="Edit Job" />
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <Translate expression="career.edit.Subtitle" />
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={handleBackToListing}
              sx={{
                'borderColor': '#FF6B35',
                'color': '#FF6B35',
                'px': 3,
                '&:hover': {
                  borderColor: '#E55A2B',
                  backgroundColor: 'rgba(255, 107, 53, 0.04)',
                },
              }}
            >
              <Translate expression="career.create.Actions.BackToListing" />
            </Button>
          </Box>
        </Box>

        {/* Stepper */}
        <Box sx={{mb: 4}}>
          <LargeTopStepper
            steps={STEPS.length}
            activeStep={activeStep}
            stepLabels={STEPS.map((step) => step.title)}
          />
        </Box>

        {/* Main Content */}
        <Box sx={{mb: 4}}>
          <CurrentStep
            newJob={newJob}
            updateJobDetails={updateJobDetails}
            updateJobSchedule={updateJobSchedule}
            updateJobPrice={updateJobPrice}
            updateJobLocation={updateJobLocation}
            updateJobStartDate={updateJobStartDate}
            updateJobCarePlan={updateJobCarePlan}
            updateJobRequirements={updateJobRequirements}
            updateJobConsiderations={updateJobConsiderations}
            updateJobPriorities={updateJobPriorities}
            isStepValid={isStepValid}
            setIsStepValid={setIsStepValid}
            statusProgressWrap={statusProgressWrap}
            selectedCaregivers={selectedCaregivers}
            setSelectedCaregivers={setSelectedCaregivers}
          />
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{
              'borderColor': '#FF6B35',
              'color': '#FF6B35',
              'px': 4,
              '&:hover': {
                borderColor: '#E55A2B',
                backgroundColor: 'rgba(255, 107, 53, 0.04)',
              },
            }}
          >
            <KeyboardArrowLeft />
            <Translate expression="career.create.Actions.Back" />
          </Button>


          <Button
            variant="contained"
            size="large"
            onClick={isLastStep ? handleSubmit : handleNext}
            disabled={!isStepValid}
            sx={{
              'backgroundColor': '#FF6B35',
              'px': 4,
              '&:hover': {
                backgroundColor: '#E55A2B',
              },
            }}
          >
            {stepText}
            <KeyboardArrowRight />
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

EditJob.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(EditJob));
